import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useGo } from '@/back';
import { PAY_PATH } from '@/covered';

import { Glyph, type GlyphName } from '@/components/icons';
import { cn } from '@/lib/utils';
import { useT } from '@/str';

const LIBRARY = /^\/(kutubxona|mavzular|belgilar|shpargalkalar|qoidalar|markazlar|avtodrom)(\/|$)/;

const NONE = /^\/(practice|history|settings|tariflar|tolov|tolovlar|qayta-aloqa)(\/|$)/;

export const MAIN_SLOT = 'main-slot';

export function Dock({ children }: { children?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--dock-h', `${el.offsetHeight}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--dock-h');
    };
  }, []);
  return (
    <div
      ref={ref}
      data-dock
      inert={PAY_PATH.test(pathname)}
      className="fixed inset-x-0 bottom-0 z-50 flex flex-col"
    >
      {children}
      <div
        id={MAIN_SLOT}
        className="bg-tg border-border border-t pb-[max(env(safe-area-inset-bottom),var(--tg-viewport-safe-area-inset-bottom,0px))] empty:border-t-0"
      />
    </div>
  );
}

export function TabBar() {
  const go = useGo();
  const { pathname } = useLocation();
  const t = useT();

  const tabs: { path: string; text: string; icon: GlyphName }[] = [
    { path: '/', text: t.tabHome, icon: 'tabHome' },
    { path: '/biletlar', text: t.biletlar, icon: 'tabBilet' },
    { path: '/kutubxona', text: t.kutubxona, icon: 'tabLibrary' },
    { path: '/exam', text: t.tabExam, icon: 'tabExam' },
  ];

  const current = NONE.test(pathname)
    ? ''
    : LIBRARY.test(pathname)
      ? '/kutubxona'
      : pathname.startsWith('/savollar') || pathname.startsWith('/saved')
        ? '/'
        : pathname;

  return (
    <nav
      aria-label={t.srMainMenu}
      className="border-border bg-card flex border-t px-[max(0px,calc((100%-600px)/2))]"
    >
      {tabs.map(({ path, text, icon }) => {
        const on = path === '/' ? current === '/' : current.startsWith(path);
        return (
          <button
            key={path}
            type="button"
            aria-current={on ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center gap-1.25 border-t-[3px] px-1 pt-2.75 pb-2.25 active:transform-none',
              on ? 'border-t-primary text-foreground' : 'text-nav-idle border-t-transparent',
            )}
            onClick={() => go(path)}
          >
            <Glyph name={icon} size={19} round={false} />
            <span className="text-t10h tracking-label font-semibold">{text}</span>
          </button>
        );
      })}
    </nav>
  );
}
